import React, { useState } from 'react';
import { Row, Col, Button, Form, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';

// ---------------------------------------------------------------------------
// Config — keeps the UI data-driven. "importable" flags which entities expose
// an import endpoint on the backend (see core/importers.py IMPORTERS dict).
// ---------------------------------------------------------------------------
const ENTITIES = [
  // Reference / master data
  { key: 'crew_members',                importable: true  },
  { key: 'customers',                   importable: true  },
  { key: 'companies',                   importable: true  },
  { key: 'vehicles_categories',         importable: true  },
  { key: 'vehicles',                    importable: true  },
  { key: 'warehouse_items_types',       importable: true  },
  { key: 'warehouse_items_categories',  importable: true  },
  { key: 'warehouse_items',             importable: true  },
  { key: 'item_bundles',                importable: true  },
  { key: 'item_bundle_entries',         importable: true  },
  { key: 'item_cases',                  importable: true  },
  { key: 'item_case_entries',           importable: true  },
  { key: 'item_units',                  importable: true  },
  // Tariff / pricing
  { key: 'tariff_types',                importable: true  },
  { key: 'tariffs',                     importable: true  },
  { key: 'vehicle_tariff_types',        importable: true  },
  { key: 'vehicle_tariffs',             importable: true  },
  // Projects
  { key: 'projects',                    importable: true  },
  { key: 'project_items',               importable: true  },
  { key: 'project_external_items',      importable: true  },
  { key: 'project_staff',               importable: true  },
  { key: 'project_vehicles',            importable: true  },
  { key: 'project_bundles',             importable: true  },
  // Contracts & rentals
  { key: 'contracts',                   importable: true  },
  { key: 'rentals',                     importable: true  },
];

// ---------------------------------------------------------------------------
// Shared helper — fetch a file with the Bearer token, then trigger download
// ---------------------------------------------------------------------------
const downloadBlobFromApi = async (endpoint, filename) => {
  // api.get with responseType:'blob' sends the Bearer token automatically
  const blob = await api.get(endpoint, { responseType: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// EntityRow — one row inside the per-entity table
// ---------------------------------------------------------------------------
const EntityRow = ({ entityKey, importable }) => {
  const { t } = useTranslation();
  const [importResult, setImportResult] = useState(null);
  const [lastFileInput, setLastFileInput] = useState(null);

  const importMutation = useMutation({
    mutationFn: ({ formData }) => api.post(`core/import/?entity=${entityKey}`, formData),
    onSuccess: (result) => setImportResult(result),
    onSettled: () => {
      if (lastFileInput) lastFileInput.value = '';
    },
  });

  const handleExport = async (fmt) => {
    try {
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      await downloadBlobFromApi(
        `core/export/?entity=${entityKey}&file_format=${fmt}`,
        `${entityKey}_${ts}.${fmt}`
      );
    } catch (err) {
      // surface error briefly — could add state if needed
      console.error('Export failed', err);
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLastFileInput(e.target);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    importMutation.mutate({ formData });
  };

  const importing = importMutation.isPending;
  const importError = importMutation.isError
    ? (importMutation.error?.message || t('settings.importExport.importFailed'))
    : null;

  return (
    <tr>
      <td className="align-middle fw-medium">
        {t(`settings.importExport.entities.${entityKey}`)}
      </td>
      <td className="align-middle">
        <div className="btn-group btn-group-sm">
          <Button variant="outline-secondary" size="sm" onClick={() => handleExport('xlsx')} title="Export XLSX">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 11l5 5l5 -5" /><path d="M12 4l0 12" />
            </svg>
            XLSX
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => handleExport('csv')} title="Export CSV">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 11l5 5l5 -5" /><path d="M12 4l0 12" />
            </svg>
            CSV
          </Button>
        </div>
      </td>
      <td className="align-middle">
        {importable ? (
          <>
            <Form.Label
              htmlFor={`import-file-${entityKey}`}
              className="btn btn-sm btn-outline-primary mb-0"
              style={{ cursor: 'pointer' }}
            >
              {importing ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 7l5 -5l5 5" /><path d="M12 4l0 12" />
                </svg>
              )}
              {t('settings.importExport.importFile')}
            </Form.Label>
            <Form.Control
              id={`import-file-${entityKey}`}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="d-none"
              onChange={handleImport}
              disabled={importing}
            />
            {importResult && (
              <div className="mt-1">
                <Badge bg="success" className="me-1">{importResult.created} {t('settings.importExport.created')}</Badge>
                <Badge bg="info" className="me-1">{importResult.updated} {t('settings.importExport.updated')}</Badge>
                {importResult.errors?.length > 0 && (
                  <Badge bg="warning" text="dark">{importResult.errors.length} {t('settings.importExport.errors')}</Badge>
                )}
              </div>
            )}
            {importResult?.errors?.length > 0 && (
              <ul className="text-warning small mt-1 mb-0 ps-3">
                {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                {importResult.errors.length > 5 && <li>…+{importResult.errors.length - 5} more</li>}
              </ul>
            )}
            {importError && <div className="text-danger small mt-1">{importError}</div>}
          </>
        ) : (
          <span className="text-muted small">{t('settings.importExport.exportOnly')}</span>
        )}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// ImportExport — main screen
// ---------------------------------------------------------------------------
const ImportExport = () => {
  const { t } = useTranslation();
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupError, setBackupError] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreError, setRestoreError] = useState(null);

  const handleBackup = async () => {
    setBackupLoading(true);
    setBackupError(null);
    try {
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      await downloadBlobFromApi('core/backup/', `backup_${ts}.zip`);
    } catch (err) {
      setBackupError(err.message || t('settings.importExport.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRestoreLoading(true);
    setRestoreResult(null);
    setRestoreError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await api.post('core/backup/', formData);
      setRestoreResult(result);
    } catch (err) {
      setRestoreError(err.message || t('settings.importExport.restoreFailed'));
    } finally {
      setRestoreLoading(false);
      e.target.value = '';
    }
  };

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.importExport.title')}
    >
      <Row className="g-4">

        {/* Full backup / restore */}
        <Col xs={12}>
          <PageSection
            title={t('settings.importExport.fullBackup.title')}
          >
            <Row className="g-4">
              {/* Download */}
              <Col xs={12} md={6}>
                <h4 className="subheader">{t('settings.importExport.fullBackup.downloadTitle')}</h4>
                <p className="text-muted mb-3">{t('settings.importExport.fullBackup.description')}</p>
                {backupError && <div className="alert alert-danger mb-3">{backupError}</div>}
                <AppButton variant="primary" onClick={handleBackup} loading={backupLoading} loadingText={t('common.loading')}>
                  {t('settings.importExport.fullBackup.download')}
                </AppButton>
              </Col>

              {/* Restore */}
              <Col xs={12} md={6}>
                <h4 className="subheader">{t('settings.importExport.fullBackup.restoreTitle')}</h4>
                <p className="text-muted mb-3">{t('settings.importExport.fullBackup.restoreDescription')}</p>
                {restoreError && <div className="alert alert-danger mb-3">{restoreError}</div>}
                <Form.Label
                  htmlFor="restore-zip-input"
                  className="btn btn-outline-warning mb-0"
                  style={{ cursor: 'pointer' }}
                >
                  {restoreLoading ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 7l5 -5l5 5" /><path d="M12 4l0 12" />
                    </svg>
                  )}
                  {t('settings.importExport.fullBackup.restore')}
                </Form.Label>
                      <Form.Control
                        id="restore-zip-input"
                        type="file"
                        accept=".zip"
                        className="d-none"
                        onChange={handleRestore}
                        disabled={restoreLoading}
                      />
                      {restoreResult && (
                        <div className="mt-3">
                          <div className="mb-2 fw-medium">{t('settings.importExport.fullBackup.restoreSummary')}</div>
                          {Object.entries(restoreResult.data || {}).map(([entity, res]) => (
                            <div key={entity} className="d-flex gap-2 align-items-center mb-1">
                              <span className="text-muted small" style={{ minWidth: 130 }}>{entity}</span>
                              <Badge bg="success">{res.created} {t('settings.importExport.created')}</Badge>
                              <Badge bg="info">{res.updated} {t('settings.importExport.updated')}</Badge>
                              {res.errors?.length > 0 && <Badge bg="warning" text="dark">{res.errors.length} {t('settings.importExport.errors')}</Badge>}
                            </div>
                          ))}
                          {restoreResult.media && (
                            <div className="text-muted small mt-2">
                              {t('settings.importExport.fullBackup.mediaRestored', { count: restoreResult.media.restored })}
                            </div>
                          )}
                          {restoreResult.skipped?.length > 0 && (
                            <div className="text-muted small">{t('settings.importExport.fullBackup.skipped')}: {restoreResult.skipped.join(', ')}</div>
                          )}
                        </div>
                      )}
              </Col>
            </Row>
          </PageSection>
        </Col>

        {/* Per-entity table */}
        <Col xs={12}>
          <PageSection title={t('settings.importExport.perEntity.title')} bodyClassName="p-0">
                  <div className="table-responsive">
                    <table className="table table-vcenter table-nowrap mb-0">
                      <thead>
                        <tr>
                          <th>{t('settings.importExport.table.entity')}</th>
                          <th>{t('settings.importExport.table.export')}</th>
                          <th>{t('settings.importExport.table.import')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ENTITIES.map(({ key, importable }) => (
                          <EntityRow key={key} entityKey={key} importable={importable} />
                        ))}
                      </tbody>
                    </table>
                  </div>
          </PageSection>
        </Col>

        {/* Import note */}
        <Col xs={12}>
          <div className="alert alert-info">
            <strong>{t('settings.importExport.importNote.title')}</strong>{' '}
            {t('settings.importExport.importNote.body')}
          </div>
        </Col>

      </Row>
    </PageShell>
  );
};

export default ImportExport;
